import { GenericAbortSignal } from 'axios';

import {
  InboundReferralPreference,
  InboundReferralPreferenceOptions,
  Notification,
  NotificationOptions,
} from 'models/Notifications';
import { UserPreferenceType } from 'models/UserPreference';
import { BaseMutationParams, BaseQueryParams } from 'services/api/types';
import http from 'services/http';

export const notificationPreferencesQueryKeys = {
  index: (params: Record<string, any> = {}) => ['notificationPreferences', 'index', params] as const,
};

// The index serves both record families in one list: the care-coordination rows (one per
// enabled group type) and the single inbound-referrals row, told apart by the wire `type`.
export type AnyNotificationPreference = Notification | InboundReferralPreference;

export function isInboundReferralPreference(
  preference: AnyNotificationPreference
): preference is InboundReferralPreference {
  return preference.type === UserPreferenceType.NOTIFICATION_INBOUND_REFERRALS;
}

type IndexParams = {
  id: string;
  client?: string;
} & BaseQueryParams;
export async function fetchNotificationPreferences({ id, ...params }: IndexParams, signal?: GenericAbortSignal) {
  return http
    .get<{ data: (NotificationOptions | InboundReferralPreferenceOptions)[] }>(`/me/notification_preferences?${id}`, {
      params,
      signal,
    })
    .then((response) =>
      response.data.data.map((preference) =>
        preference.type === UserPreferenceType.NOTIFICATION_INBOUND_REFERRALS
          ? new InboundReferralPreference(preference as InboundReferralPreferenceOptions)
          : new Notification(preference as NotificationOptions)
      )
    );
}

export async function createNotificationPreferences(params: NotificationOptions & BaseMutationParams) {
  return http
    .post<NotificationOptions>(`/me/notification_preferences`, params)
    .then((response) => new Notification(response.data));
}

type UpdateParams = {
  urlExtension: string;
} & NotificationOptions &
  BaseMutationParams;
export async function updateNotificationPreferences({ urlExtension, ...params }: UpdateParams) {
  return http
    .put<NotificationOptions>(`/me/notification_preferences${urlExtension}`, params)
    .then((response) => new Notification(response.data));
}

// The inbound-referrals row submits its scopes flat: referral sources as the display-name
// strings the api stores verbatim, groups as ids (the api hydrates them back on every read).
export type InboundReferralPreferenceMutationParams = {
  id?: string;
  clientId: string;
  active: boolean;
  type: UserPreferenceType.NOTIFICATION_INBOUND_REFERRALS;
  notificationSelections: InboundReferralPreferenceOptions['notificationSelections'];
  scopes: {
    referralSources: string[];
    groups: string[];
  };
};

export async function createInboundReferralPreference(params: InboundReferralPreferenceMutationParams) {
  return http
    .post<InboundReferralPreferenceOptions>(`/me/notification_preferences`, params)
    .then((response) => new InboundReferralPreference(response.data));
}

export async function updateInboundReferralPreference({ id, ...params }: InboundReferralPreferenceMutationParams) {
  return http
    .put<InboundReferralPreferenceOptions>(`/me/notification_preferences/${id}`, params)
    .then((response) => new InboundReferralPreference(response.data));
}
